/**
 * [LGP 2026-10-15, user] NEW FILE — Lead Generator Performance → Team Roster: the Apps Script page "Team Roster" (Index.html
 * data-page="team-roster" + the Add member form; JavaScript.html loadRoster, onRoster, renderRoster, rSortBy, editText,
 * editSelect, changeRosterValue, openAddMember … submitAddMember). The roster now lives in our database
 * (Team_Email_Marketing_Reporting_Structure — user: "a. Our database table becomes the live roster"); anyone with this tab may
 * edit it (user: "admin only and the person having the access for this tab"). Every row with a Status; Name, Team Leader Name,
 * Team Manager, Status and ROW Status edit in the table (Email is locked); + Add member asks for all six fields.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import DataTable from '../../components/DataTable';
import GlassSelect from '../../components/GlassSelect';
import { fluidDialog, fluidScrim } from '../../components/Fluid';
import { usePresence } from '../../hooks/usePresence';
import { fluid } from '../../styles/motion';
import { lgSurface, lgSquircle, lgSunken, lgControl, lgPress, lgGreen } from '../../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { addLgpRosterMember, checkLgpRosterEmail, fetchLgpRoster, LgpMember, LgpRosterColumn, LgpRosterReport, LgpRosterRow, saveLgpRosterValue } from '../../services/api';
import { Column, SortConfig } from '../../types';
import {
  ViewWrap, ViewBar, PrimaryBtn, SecondaryBtn, Toolbar, SearchInput, StatusLine, TableScroll, Muted, dark,
  errorText, textCompare, ViewProps, PersonPatch,
} from './lgpShared';

/** [JavaScript.html R_COLS] Mirrors the server's columns so the skeleton shows the real headers. */
const R_COLS: LgpRosterColumn[] = [
  { key: 'name', col: 'A', label: 'Name', editable: true, type: 'text' },
  { key: 'email', col: 'B', label: 'Email', editable: false, type: 'email' },
  { key: 'leader', col: 'C', label: 'Team Leader Name', editable: true, type: 'select' },
  { key: 'manager', col: 'D', label: 'Team Manager', editable: true, type: 'select' },
  { key: 'status', col: 'F', label: 'Status', editable: true, type: 'select' },
  { key: 'rowStatus', col: 'G', label: 'ROW Status', editable: true, type: 'select' },
];
type RKey = LgpRosterColumn['key'];

/** [JavaScript.html statusRank] Active before Inactive, anything else last. */
const statusRank = (v: string) => { const s = String(v || '').trim().toLowerCase(); return s === 'active' ? 0 : s === 'inactive' ? 1 : 2; };

/* ---- The input rules — the same as the server's (Roster.js emailProblem_ / cleanName_ / nameProblem_) ---- */
const INVISIBLE_CLASS = '\\u00AD\\u034F\\u061C\\u115F\\u1160\\u17B4\\u17B5\\u180B-\\u180F'
  + '\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u206F\\u3164\\uFE00-\\uFE0F\\uFEFF\\uFFA0\\uFFF0-\\uFFF8';
const unicodeRegex = (source: string, flags: string, fallback: RegExp) => { try { return new RegExp(source, flags); } catch { return fallback; } };
const INVISIBLE_RE = unicodeRegex('\\p{Default_Ignorable_Code_Point}', 'gu', new RegExp(`[${INVISIBLE_CLASS}]`, 'g'));
const EDGE_JUNK_RE = unicodeRegex('^[\\s\\p{Default_Ignorable_Code_Point}]+|[\\s\\p{Default_Ignorable_Code_Point}]+$', 'gu',
  new RegExp(`^[\\s${INVISIBLE_CLASS}]+|[\\s${INVISIBLE_CLASS}]+$`, 'g'));
const LETTER_RE = unicodeRegex('\\p{L}', 'u', /[A-Za-zÀ-ɏ]/);
const EMAIL_PATTERN = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
/** [JavaScript.html emailOk] Plain-ASCII address in the browser's own email shape; no leading = + - '. */
const emailOk = (email: string) => EMAIL_PATTERN.test(email) && !/^[=+\-']/.test(email);
/** [JavaScript.html cleanName] */
const cleanName = (value: unknown) => String(value == null ? '' : value).replace(EDGE_JUNK_RE, '').replace(/\s+/g, ' ');
/** [JavaScript.html hasRealLetter] */
const hasRealLetter = (name: string) => LETTER_RE.test(String(name).replace(INVISIBLE_RE, ''));
/** [JavaScript.html joinNames] 'a', 'a and b', 'a, b and c' */
const joinNames = (names: string[]) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] || ''));

const NameInput = styled.input`
  width: 100%;
  min-width: 170px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: ${(p) => p.theme.text};
  font: inherit;
  font-size: 13px;
  &:hover:not(:disabled) { border-color: ${(p) => p.theme.line}; }
  /* [LIQUID-GLASS 2026-11-02] while typing: pressed into the glass, a soft green ring and glow */
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    background: ${(p) => (dark(p) ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
    box-shadow: ${(p) => (dark(p) ? 'inset 0 1px 2px rgba(0,0,0,0.5)' : 'inset 0 1px 2px rgba(22,25,23,0.14)')},
      0 0 0 3px color-mix(in srgb, ${lgGreen} 16%, transparent);
  }
`;
/* [LGP-COLOURS 2026-10-15, user] "make the colour of us and row different in the team roster so that we can see it easily" and
   (the same day, replacing a Payments-style green / red row tint) "remove the row wise colour, just make the active green and
   inactive text red": the rows keep their plain look; in the cells Active reads green and Inactive red, and US / ROW wear blue /
   violet — the colours the app already gives them (Monthly Performance's tags, Payments' Team ROW violet). Only the TEXT is
   coloured (user, the same day: "for the us row dropdown the text colour will remain same but remove the outer colour from the
   dropdown") — every dropdown keeps the app's own frame. The dropdown draws its own button, so the colour is set on it here. */
type Tone = 'active' | 'inactive' | 'us' | 'row';
/** [light, dark] — Monthly Performance's (US) / (ROW) tag colours */
const SIDE_INK: Record<'us' | 'row', [string, string]> = { us: ['#1d4ed8', '#93c5fd'], row: ['#6d28d9', '#c4b5fd'] };
const CellSelect = styled.div<{ $tone?: Tone }>`
  min-width: 150px;
  & > * { width: 100%; }
  ${(p) => {
    const ink = p.$tone === 'active' ? p.theme.greenInk : p.$tone === 'inactive' ? p.theme.danger
      : p.$tone === 'us' || p.$tone === 'row' ? SIDE_INK[p.$tone][dark(p) ? 1 : 0] : '';
    return ink ? `& button[aria-haspopup='listbox'] { color: ${ink}; font-weight: 700; }` : '';
  }}
`;

/** One editable Name cell: saves on Enter or when the box is left (the script's `change` event), never per key. */
const NameCell: React.FC<{ row: LgpRosterRow; disabled: boolean; onCommit: (row: LgpRosterRow, value: string, reset: () => void) => void }> = ({ row, disabled, onCommit }) => {
  const [v, setV] = useState(row.name || '');
  useEffect(() => { setV(row.name || ''); }, [row.name]);
  const commit = () => onCommit(row, v, () => setV(row.name || ''));
  return (
    <NameInput value={v} disabled={disabled} data-name-row={row.row}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
};

/* ---- Add member ---- */
const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(p) => (dark(p) ? 'rgba(10, 10, 10, 0.62)' : 'rgba(22, 25, 23, 0.42)')};
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  ${(p) => fluidScrim(p.$open)}
`;
const Box = styled.form<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  width: 460px;
  max-width: 100%;
  max-height: calc(100vh / var(--app-zoom, 1) - 32px); /* [APP-FIT 2026-10-26] the window in page px */
  /* [LIQUID-GLASS 2026-11-02, user] the frosted glass of the app's windows (Edit Mapping, Add Payment): a column whose middle
     (.body) scrolls — the glass itself never scrolls; a round glass close button; the boxes pressed in */
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px 22px;
  color: ${(p) => p.theme.text};
  ${lgSurface(34)}
  ${lgSquircle(22)}
  .head { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .head h2 { margin: 0; font-size: 17px; font-weight: 800; }
  .x {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    color: ${(p) => p.theme.text};
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    ${lgControl}
    ${lgPress}
  }
  .body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; margin: 0 -6px; padding: 2px 6px; }
  .actions { flex-shrink: 0; display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
`;
const MField = styled.label<{ $missing?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 11px;
  > span.lab { font-size: 11.5px; font-weight: 800; letter-spacing: 0.3px; text-transform: uppercase; color: ${(p) => p.theme.dim}; }
  /* [LIQUID-GLASS 2026-11-02] the boxes are pressed into the glass (a missing one keeps its red edge) */
  input {
    height: 36px;
    padding: 0 12px;
    border-radius: 10px;
    ${lgSunken}
    color: ${(p) => p.theme.text};
    font: inherit;
    font-size: 13.5px;
    outline: none;
  }
  && input { ${(p) => (p.$missing ? `border-color: ${p.theme.danger};` : '')} }
  .sel { border-radius: 10px; ${(p) => (p.$missing ? `box-shadow: 0 0 0 1px ${p.theme.danger};` : '')} }
  .sel button[aria-haspopup='listbox'] {
    border-color: transparent;
    background: ${(p) => (dark(p) ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
    box-shadow: ${(p) => (dark(p)
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
  }
  .sel > * { width: 100%; }
  .note { font-size: 12px; font-weight: 600; }
  .note.ok { color: ${(p) => p.theme.greenInk}; }
  .note.warn { color: ${(p) => p.theme.danger}; }
  .note.info { color: ${(p) => (dark(p) ? '#93c5fd' : '#1d4ed8')}; }
`;
const AddError = styled.p<{ $soft: boolean }>`
  margin: 6px 0 0;
  font-size: 12.5px;
  font-weight: 700;
  color: ${(p) => p.theme.danger};
  ${(p) => (p.$soft ? '' : `padding: 9px 12px; border-radius: 10px; background: ${dark(p) ? 'rgba(248,113,113,0.12)' : 'rgba(220,38,38,0.08)'};`)}
`;

/** [JavaScript.html ADD_FIELDS] The six fields in form order, with the name the message uses for each. */
const ADD_FIELDS: [keyof LgpMember, string][] = [
  ['name', 'Name'], ['email', 'Email'], ['leader', 'Team Leader Name'], ['manager', 'Team Manager'], ['status', 'Status'], ['rowStatus', 'ROW Status'],
];
const EMPTY_MEMBER: LgpMember = { name: '', email: '', leader: '', manager: '', status: '', rowStatus: '' };

const AddMember: React.FC<{ open: boolean; options: Record<string, string[]>; onClose: () => void; onAdded: (data: LgpRosterReport, m: LgpMember) => void }> = ({ open, options, onClose, onAdded }) => {
  const pres = usePresence(open, fluid.dialogOutMs);
  const [m, setM] = useState<LgpMember>(EMPTY_MEMBER);
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<{ text: string; soft: boolean } | null>(null);
  const [emailNote, setEmailNote] = useState<{ text: string; kind: '' | 'ok' | 'warn' | 'info' }>({ text: '', kind: '' });
  const [saving, setSaving] = useState(false);
  const check = useRef<{ timer: number | null; result: any }>({ timer: null, result: null });
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // [JavaScript.html openAddMember] a clean form each time, the cursor in Name
  useEffect(() => {
    if (!open) return undefined;
    setM(EMPTY_MEMBER); setMissing(new Set()); setError(null); setEmailNote({ text: '', kind: '' }); check.current.result = null; setSaving(false);
    const t = window.setTimeout(() => nameRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { window.clearTimeout(t); document.removeEventListener('keydown', onKey); if (check.current.timer) window.clearTimeout(check.current.timer); };
  }, [open, onClose]);

  /** [JavaScript.html clearMissing] a filled field loses its outline; the hint goes when none are left */
  const setField = (k: keyof LgpMember, v: string) => {
    setM((cur) => ({ ...cur, [k]: v }));
    if (String(v || '').trim()) {
      setMissing((cur) => {
        if (!cur.has(k)) return cur;
        const next = new Set(cur); next.delete(k);
        if (!next.size) setError((e) => (e && e.soft ? null : e));
        return next;
      });
    }
  };

  /** [JavaScript.html onAddEmailInput] the live duplicate check — debounced, a stale reply ignored */
  const onEmail = (raw: string) => {
    setField('email', raw);
    const value = raw.trim();
    if (check.current.timer) window.clearTimeout(check.current.timer);
    setError(null);
    if (!value) { setEmailNote({ text: '', kind: '' }); check.current.result = null; return; }
    if (!emailOk(value)) { setEmailNote({ text: 'Enter a complete email address.', kind: 'warn' }); check.current.result = null; return; }
    setEmailNote({ text: 'Checking…', kind: '' });
    check.current.timer = window.setTimeout(() => {
      const asked = value;
      checkLgpRosterEmail(asked)
        .then((res) => {
          if ((emailRef.current?.value || '').trim() !== asked) return;      // stale reply
          check.current.result = res;
          if (!res.exists) setEmailNote({ text: 'Available.', kind: 'ok' });
          else if (res.hasStatus) setEmailNote({ text: `Already exists on the roster (row ${res.row} — ${res.name || 'no name'}). Edit that row instead.`, kind: 'warn' });
          else setEmailNote({ text: `This email is already in the roster (row ${res.row}) without a status. Saving will complete that row rather than add a new one.`, kind: 'info' });
        })
        .catch(() => { if ((emailRef.current?.value || '').trim() === asked) setEmailNote({ text: 'Could not check right now.', kind: 'warn' }); });
    }, 200);
  };

  /** [JavaScript.html submitAddMember] nothing is sent until every field has a value; the server re-checks all six */
  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const member: LgpMember = { ...m, name: cleanName(m.name), email: m.email.trim() };
    const values: Record<string, string> = { ...member, name: hasRealLetter(member.name) ? member.name : '' };
    const gaps = ADD_FIELDS.filter(([k]) => !values[k]);
    setMissing(new Set(gaps.map(([k]) => k)));
    if (gaps.length) {
      setError({ text: `Please complete all fields to add this member — missing: ${joinNames(gaps.map(([, label]) => label))}.`, soft: true });
      if (gaps[0][0] === 'name') nameRef.current?.focus(); else if (gaps[0][0] === 'email') emailRef.current?.focus();
      return;
    }
    if (!emailOk(member.email)) {
      setMissing(new Set(['email']));
      setError({ text: 'Please enter a valid email address to add this member.', soft: true });
      emailRef.current?.focus();
      return;
    }
    const known = check.current.result;
    if (known && known.exists && known.hasStatus) {
      setError({ text: `That email is already on the roster (row ${known.row}). Edit that person in the table instead.`, soft: false });
      return;
    }
    setSaving(true);
    setError(null);
    addLgpRosterMember(member)
      .then((data) => { setSaving(false); onAdded(data, member); })
      .catch((err) => { setSaving(false); setError({ text: errorText(err), soft: false }); });
  };

  if (!pres.mounted) return null;
  const isOpen = open && pres.entered;
  const select = (k: 'leader' | 'manager' | 'status' | 'rowStatus') => (
    <div className="sel" data-add-field={k}>
      <GlassSelect liquidList value={m[k]} onChange={(e) => setField(k, e.target.value)}>
        <option value="">Select…</option>
        {(options[k] || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </GlassSelect>
    </div>
  );
  return createPortal(
    <Overlay $open={isOpen} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Box $open={isOpen} noValidate onSubmit={submit} data-add-member="">
        <div className="head"><h2>Add member</h2><button type="button" className="x" onClick={onClose} aria-label="Close" data-lg-press="">×</button></div>
        <div className="body">{/* [LIQUID-GLASS 2026-11-02] only the fields scroll */}
        <MField $missing={missing.has('name')}><span className="lab">Name</span>
          <input ref={nameRef} value={m.name} autoComplete="off" onChange={(e) => setField('name', e.target.value)} data-add-field="name" /></MField>
        <MField $missing={missing.has('email')}><span className="lab">Email</span>
          <input ref={emailRef} type="email" value={m.email} autoComplete="off" autoCapitalize="off" spellCheck={false}
            onChange={(e) => onEmail(e.target.value)} data-add-field="email" />
          {emailNote.text && <span className={`note ${emailNote.kind}`} data-email-note="">{emailNote.text}</span>}</MField>
        <MField as="div" $missing={missing.has('leader')}><span className="lab">Team Leader Name</span>{select('leader')}</MField>
        <MField as="div" $missing={missing.has('manager')}><span className="lab">Team Manager</span>{select('manager')}</MField>
        <MField as="div" $missing={missing.has('status')}><span className="lab">Status</span>{select('status')}</MField>
        <MField as="div" $missing={missing.has('rowStatus')}><span className="lab">ROW Status</span>{select('rowStatus')}</MField>
        {error && <AddError $soft={error.soft} aria-live="polite" data-add-error="">{error.text}</AddError>}
        </div>
        <div className="actions">
          <SecondaryBtn type="button" onClick={onClose}>Cancel</SecondaryBtn>
          <PrimaryBtn type="submit" disabled={saving} data-add-save="">Add member</PrimaryBtn>
        </div>
      </Box>
    </Overlay>,
    document.body,
  );
};

/* ---- The view ---- */

const RosterView: React.FC<ViewProps & { onPatchPerson: (p: PersonPatch) => void }> = ({ active, noDock, storageKey, onVersion, register, onPatchPerson }) => {
  const [roster, setRoster] = useState<LgpRosterReport | null>(null);
  const [busy, setBusy] = useState(false);                  // a save is on its way: the cells are locked
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>({ text: 'Reading data…' });
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<RKey>('status');   // Active first, then Inactive
  const [sortAsc, setSortAsc] = useState(true);
  const [adding, setAdding] = useState(false);
  const req = useRef(0);
  const rosterRef = useRef(roster);
  rosterRef.current = roster;

  /** [JavaScript.html onRoster] */
  const onRoster = useCallback((data: LgpRosterReport, msg?: string) => {
    setRoster(data);
    setBusy(false);
    setNote(msg ? { text: msg } : null);
    onVersion(data.meta.version, 'team-roster');
  }, [onVersion]);

  /** [JavaScript.html loadRoster] */
  const load = useCallback((silent: boolean) => {
    if (!silent) setNote({ text: 'Reading data…' });
    const n = ++req.current;
    fetchLgpRoster()
      .then((data) => { if (n === req.current) onRoster(data); })
      .catch((err) => { if (n !== req.current || silent) return; setNote({ text: errorText(err), error: true }); });
  }, [onRoster]);
  const loadRef = useRef(load);
  loadRef.current = load;

  const inited = useRef(false);
  useEffect(() => {
    if (!active || inited.current) return;
    inited.current = true;
    loadRef.current(false);
    register('team-roster', { refresh: () => loadRef.current(true) });
  }, [active, register]);

  /** [JavaScript.html changeRosterValue] Leads Count / Monthly Performance move the person now; the save confirms it. */
  const change = useCallback((row: LgpRosterRow, key: RKey, raw: string, reset?: () => void) => {
    const value = String(raw ?? '').trim();
    const previous = String((row as any)[key] || '');
    if (value === previous) { if (reset) reset(); return; }
    if (value === '') {                                   // a cleared box is not a save
      if (reset) reset();
      setNote({ text: 'That field cannot be empty.', error: true });
      return;
    }
    const after: LgpRosterRow = { ...row, [key]: value };
    onPatchPerson(after);
    setBusy(true);
    setNote({ text: 'Saving to the roster…' });
    ++req.current;                                        // an older roster load must not land after this
    const label = (rosterRef.current?.columns || R_COLS).find((c) => c.key === key)?.label || key;
    saveLgpRosterValue(row.row, row.email, key, value)
      .then((data) => {
        ++req.current;                                    // nor one started while this save ran
        onRoster(data, `Saved · ${row.name || row.email || `row ${row.row}`} · ${label} → ${value}`);
      })
      .catch((err) => {
        setBusy(false);
        if (reset) reset();
        onPatchPerson(row);                               // the person back on the report pages
        setNote({ text: errorText(err), error: true });
      });
  }, [onPatchPerson, onRoster]);

  /** [JavaScript.html rSortBy] */
  const onSort = useCallback((key: string) => {
    const k = key as RKey;
    if (k === sortKey) setSortAsc((a) => !a);
    else { setSortKey(k); setSortAsc(true); }
  }, [sortKey]);

  /** [JavaScript.html renderRoster] */
  const rows = useMemo(() => {
    if (!roster) return [];
    const q = search.trim().toLowerCase();
    const list = roster.rows.filter((r) => !q || roster.columns.some((c) => String((r as any)[c.key]).toLowerCase().includes(q)));
    return list.slice().sort((a, b) => {
      let cmp: number;
      if (sortKey === 'status') {
        cmp = statusRank(a.status) - statusRank(b.status);
      } else {
        // Blanks go last in either direction.
        const av = String((a as any)[sortKey] || ''); const bv = String((b as any)[sortKey] || '');
        if (!av !== !bv) return av ? -1 : 1;
        cmp = textCompare(av, bv);
      }
      // Within a group keep a stable, readable order.
      if (!cmp) cmp = textCompare(String(a.leader || '~'), String(b.leader || '~')) || textCompare(String(a.name || ''), String(b.name || ''));
      return sortAsc ? cmp : -cmp;
    }).map((r) => ({ ...r, id: `r${r.row}` }));
  }, [roster, search, sortKey, sortAsc]);

  const columns: Column[] = useMemo(() => (roster?.columns || R_COLS).map((c) => ({
    key: c.key,
    label: c.label,
    sortable: true,
    render: (v: string, row: LgpRosterRow) => {
      if (c.editable && c.type === 'select') {
        const current = String(v || '');
        const opts = ((roster?.options || {})[c.key] || []).slice();
        if (current && !opts.includes(current)) opts.unshift(current);   // a value already there stays visible
        const tone: Tone | undefined = c.key === 'status' ? (/^active$/i.test(current) ? 'active' : /^inactive$/i.test(current) ? 'inactive' : undefined)
          : c.key === 'rowStatus' ? (/^row$/i.test(current) ? 'row' : /^us$/i.test(current) ? 'us' : undefined) : undefined;
        return (
          <CellSelect $tone={tone} data-cell={`${c.key}:${row.row}`} data-tone={tone || ''}>
            <GlassSelect liquidList /* [LIQUID-GLASS 2026-11-02] the list that opens is glass; the cell stays calm */ value={current} disabled={busy} onChange={(e) => change(row, c.key, e.target.value)}>
              {!current && <option value="">—</option>}
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </GlassSelect>
          </CellSelect>
        );
      }
      if (c.editable) return <NameCell row={row} disabled={busy} onCommit={(r, value, reset) => change(r, 'name', value, reset)} />;
      return v ? String(v) : <Muted>—</Muted>;
    },
  })), [roster, busy, change]);

  const m = roster?.meta;
  const bits = m ? Object.keys(m.statuses).sort().map((s) => `${m.statuses[s]} ${s}`)
    .concat(Object.keys(m.rowStatuses || {}).sort().map((s) => `${m.rowStatuses[s]} ${s}`)) : [];
  const status = note ? note : m ? {
    text: `${m.people} people · ${bits.join(' · ')}${m.skippedNoStatus ? ` · ${m.skippedNoStatus} rows without a status hidden` : ''}`,
  } : { text: '' };
  const sort: SortConfig = { sortBy: sortKey, sortOrder: sortAsc ? 'asc' : 'desc' };
  const close = useCallback(() => setAdding(false), []);

  return (
    <ViewWrap data-lgp-view="team-roster">
      <ViewBar>
        <span className="sub">Team roster · Team Email Marketing Reporting Structure (the live roster)</span>
        <div className="controls">
          <PrimaryBtn type="button" disabled={!roster} onClick={() => setAdding(true)} data-add-open="">+ Add member</PrimaryBtn>
        </div>
      </ViewBar>
      <Toolbar>
        <div className="left">
          <SearchInput type="search" placeholder="Filter by name, email, team or status" value={search} disabled={!roster || busy} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <StatusLine $error={!!status.error} data-status="">{status.text}</StatusLine>
      </Toolbar>
      <TableScroll $noDock={noDock}>
        <DataTable
          columns={columns}
          data={rows}
          sort={sort}
          onSort={onSort}
          loading={!roster && !note?.error}
          disableMobileCards
          parentScroll
          resizeStorageKey={`dt-lgp-roster-colw:${storageKey}`}
          empty={{ title: !roster ? (note?.text || '') : roster.rows.length ? 'No one matches that filter.' : 'No roster rows with a status.' }}
        />
      </TableScroll>
      <AddMember
        open={adding}
        options={roster?.options || {}}
        onClose={close}
        onAdded={(data, member) => {
          setAdding(false);
          ++req.current;
          onRoster(data, `Added · ${member.name} · ${member.email}`);
          const added = data.rows.find((x) => x.email.toLowerCase() === member.email.toLowerCase());
          if (added) onPatchPerson(added);                 // …and Leads Count shows them now
        }}
      />
    </ViewWrap>
  );
};

export default RosterView;
